/**
 * Sanitiza metadata customizada para serialização segura.
 */

const MAX_DEPTH = 5;
const MAX_STRING = 2000;
const MAX_ARRAY = 100;
const MAX_BYTES = 8_192;

export interface SanitizeMetadataOptions {
  maxDepth?: number;
  maxBytes?: number;
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function sanitizeValue(
  value: unknown,
  depth: number,
  opts: SanitizeMetadataOptions,
  seen: WeakSet<object>
): unknown {
  if (depth > (opts.maxDepth ?? MAX_DEPTH)) return '[MaxDepth]';
  if (value == null) return value;
  if (typeof value === 'function') return undefined;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'symbol') return undefined;
  if (typeof value === 'string') {
    return value.length > MAX_STRING ? value.slice(0, MAX_STRING) : value;
  }
  if (typeof value !== 'object') return value;
  if (typeof Element !== 'undefined' && value instanceof Element) {
    return { _dom: (value as Element).tagName };
  }
  if (seen.has(value as object)) return '[Circular]';
  seen.add(value as object);
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY)
      .map(v => sanitizeValue(v, depth + 1, opts, seen));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const sv = sanitizeValue(v, depth + 1, opts, seen);
    if (sv !== undefined) out[k] = sv;
  }
  return out;
}

export function sanitizeMetadata(
  metadata: Record<string, unknown> | undefined,
  options: SanitizeMetadataOptions = {}
): Record<string, unknown> | undefined {
  if (!metadata || typeof metadata !== 'object') return undefined;
  const seen = new WeakSet<object>();
  const result = sanitizeValue(metadata, 0, options, seen) as Record<
    string,
    unknown
  >;
  if (!result || typeof result !== 'object') return undefined;
  const serialized = JSON.stringify(result);
  const maxBytes = options.maxBytes ?? MAX_BYTES;
  if (byteLength(serialized) > maxBytes) {
    return { _truncated: true, preview: serialized.slice(0, 500) };
  }
  return result;
}
